import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import LogoIcon from "@/components/LogoIcon";
import { useTranslation } from 'react-i18next';

const ValidateMagicLink = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'validating' | 'success' | 'error'>('validating');
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();

  // Função para validar e extrair path seguro do redirect (previne open redirect attacks)
  const getSafeRedirectPath = (url: string | null): string => {
    if (!url) return '/preferences';
    
    try {
      // Se já é um path relativo válido, usar diretamente
      if (url.startsWith('/') && !url.startsWith('//')) {
        return url;
      }
      
      // Se é URL absoluta, extrair apenas o pathname
      const parsed = new URL(url);
      
      // Validação de segurança: só permitir redirecionamento para o mesmo domínio
      if (parsed.hostname !== window.location.hostname) {
        console.warn('[ValidateMagicLink] Blocked redirect to external domain:', parsed.hostname);
        return '/preferences';
      }
      
      // Retornar apenas o path (sem domínio)
      return parsed.pathname + parsed.search;
    } catch {
      console.warn('[ValidateMagicLink] Invalid redirect URL:', url);
      return '/preferences';
    }
  };

  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get('token');
      const redirectTo = searchParams.get('redirect_to');

      if (!token) {
        setStatus('error');  
        setErrorMessage(t('validateMagicLink.error.tokenNotProvided'));
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('validate-magic-link', {
          body: { token }
        });

        if (error) throw error;

        if (data?.error) {
          setStatus('error');
          setErrorMessage(data.error);
          return;
        }

        if (data?.session?.token) {
          // Store complete session data in localStorage (including user info for consistency)
          localStorage.setItem('newsletter_session', JSON.stringify({
            user_id: data.user?.id || '',
            email: data.user?.email || '',
            session_token: data.session.token,
            expires_at: data.session.expires_at
          }));
          setStatus('success');

          // Verify token was saved and redirect after longer delay for better sync
          setTimeout(() => {
            const savedToken = localStorage.getItem('newsletter_session');
            console.log('[ValidateMagicLink] Token saved:', !!savedToken);
            
            const safePath = getSafeRedirectPath(redirectTo);
            console.log('[ValidateMagicLink] Redirecting to:', safePath);
            navigate(safePath);
          }, 2500);
        } else {
          setStatus('error');
          setErrorMessage(t('validateMagicLink.error.invalidResponse'));
        }

      } catch (error: any) {
        console.error('Error validating magic link:', error);
        setStatus('error');
        setErrorMessage(error.message || t('validateMagicLink.error.validationError'));
      }
    };

    validateToken();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-newsletter-light via-background to-newsletter-light/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <LogoIcon />
          </div>
          
          {status === 'validating' && (
            <>
              <CardTitle className="text-2xl">{t('validateMagicLink.validating.title')}</CardTitle>
              <CardDescription>
                {t('validateMagicLink.validating.description')}
              </CardDescription>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CardTitle className="text-2xl text-green-600">{t('validateMagicLink.success.title')}</CardTitle>
              <CardDescription>
                {t('validateMagicLink.success.description')}
              </CardDescription>
            </>
          )}
          
          {status === 'error' && (
            <>
              <CardTitle className="text-2xl text-destructive">{t('validateMagicLink.error.title')}</CardTitle>
              <CardDescription>
                {errorMessage || t('validateMagicLink.error.description')}
              </CardDescription>
            </>
          )}
        </CardHeader>
        
        <CardContent className="flex justify-center">
          {status === 'validating' && (
            <Loader2 className="h-8 w-8 animate-spin text-newsletter-primary" />
          )}
          
          {status === 'success' && (
            <CheckCircle className="h-8 w-8 text-green-600" />
          )}
          
          {status === 'error' && (
            <XCircle className="h-8 w-8 text-destructive" />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ValidateMagicLink;